import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIDropdown from 'UIComponents/dropdown/UIDropdown';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UITypeahead from 'UIComponents/typeahead/UITypeahead';
import { Fragment, Slice } from 'prosemirror-model';
import { useCallback, useContext, useEffect, useRef } from 'react';
import usePrevious from 'react-utils/hooks/usePrevious';
import { GenericNotFound } from '../components/GenericNotFound';
import { EditorConfigContext } from '../contexts/EditorConfigContext';
import useDebouncedEditorStateContext from '../contexts/useDebouncedEditorStateContext';
import { PERSONALIZATION_TOKEN } from '../editorCapabilities/constants';
import { FocusPlugin } from '../plugins/FocusPlugin';
import useContentSearch from '../utils/useContentSearch';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const noop = () => {};
function transformOptions(data) {
  return data && data.map(token => ({
    text: token.displayText,
    value: token.tokenKey,
    tokenStyles: token.tokenStyles
  }));
}
function PersonalizationTokenPopover() {
  const {
    [PERSONALIZATION_TOKEN]: personalizationTokenConfig
  } = useContext(EditorConfigContext);
  const {
    fetchTokens
  } = personalizationTokenConfig;
  const {
    editorState,
    setEditorState
  } = useDebouncedEditorStateContext();
  const [__data, searchQuery, setSearchQuery, options, isLoading] = useContentSearch(fetchTokens, transformOptions, noop);
  const _inputRef = useRef(null);
  const prevOption = usePrevious(options);
  const wasLoading = usePrevious(isLoading);
  useEffect(() => {
    const didPreviouslyRenderTypeahead = prevOption && prevOption.length > 0;
    const isTypeaheadRendered = options && options.length > 0;
    if ((!didPreviouslyRenderTypeahead && isTypeaheadRendered || wasLoading && !isLoading) && _inputRef && _inputRef.current) {
      _inputRef.current.focus();
    }
  }, [options, _inputRef, isLoading, wasLoading, prevOption]);
  const handleSearch = useCallback(e => setSearchQuery(e.target.value), [setSearchQuery]);
  const handleChange = useCallback(e => {
    const tokenKey = e.target.value;
    const selectedOption = options === null || options === void 0 ? void 0 : options.find(option => option.value === tokenKey);
    const node = editorState.schema.nodes.personalization_token.create({
      displayText: selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.text,
      tokenKey,
      tokenStyles: selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.tokenStyles
    });
    const tr = editorState.tr.replaceSelection(new Slice(Fragment.fromArray([node]), 0, 0)).setMeta(FocusPlugin, {
      focusView: true,
      focusViewPos: editorState.selection.$head
    });
    setEditorState(editorState.apply(tr));
  }, [options, editorState, setEditorState]);
  if (isLoading && searchQuery.length === 0) {
    return /*#__PURE__*/_jsx(UILoadingSpinner, {});
  }
  if (!options) {
    return /*#__PURE__*/_jsx("div", {
      className: "m-all-2",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "hubspot-prosemirror.personalizationToken.popover.error"
      })
    });
  }
  if (options.length === 0 && !isLoading) {
    return /*#__PURE__*/_jsx(GenericNotFound, {});
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(UITypeahead, {
      autoFilter: false,
      options: options,
      placeholder: I18n.text('hubspot-prosemirror.personalizationToken.popover.searchPlaceholder'),
      resultsClassName: "content-popover-search-results",
      searchClassName: "content-popover-search",
      onChange: handleChange,
      search: searchQuery,
      onSearchChange: handleSearch,
      inputRef: _inputRef,
      hideResults: isLoading
    }), isLoading ? /*#__PURE__*/_jsx(UILoadingSpinner, {}) : null]
  });
}
PersonalizationTokenPopover.displayName = 'PersonalizationTokenPopover';
function InsertPersonalizationToken({
  buttonText
}) {
  return /*#__PURE__*/_jsx(UIDropdown, {
    "data-test-id": "personalization-token",
    buttonUse: "transparent",
    buttonClassName: "p-all-0",
    menuWidth: 400,
    buttonText: buttonText || /*#__PURE__*/_jsx(FormattedMessage, {
      message: "hubspot-prosemirror.personalizationToken.toolbarButton"
    }),
    Content: PersonalizationTokenPopover
  });
}
InsertPersonalizationToken.displayName = 'InsertPersonalizationToken';
export default InsertPersonalizationToken;